import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, Loader2, AlertCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { didService, TalkResponse } from '../services/didService';

interface AIAvatarProps {
  isPlaying: boolean;
  onPlayPause: () => void;
  currentContent: string;
  onError?: (error: string) => void;
}

export default function AIAvatar({ isPlaying, onPlayPause, currentContent, onError }: AIAvatarProps) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [currentTalkId, setCurrentTalkId] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Generate video when content changes and playing starts
  useEffect(() => {
    if (isPlaying && currentContent && !videoUrl && !isGenerating) {
      generateVideo(currentContent);
    }
  }, [isPlaying, currentContent, videoUrl, isGenerating]);

  // Control video playback
  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying && videoUrl) {
        videoRef.current.play().catch(console.error);
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying, videoUrl]);

  // Control audio
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = !audioEnabled;
    }
  }, [audioEnabled]);

  const generateVideo = async (text: string) => {
    if (!text.trim()) return;

    setIsGenerating(true);
    setError(null);

    try {
      // Create the talk
      const talkResponse = await didService.createTalk(text);
      setCurrentTalkId(talkResponse.id);

      // Wait for completion
      const completedTalk = await didService.waitForCompletion(talkResponse.id);
      
      if (completedTalk.result_url) {
        setVideoUrl(completedTalk.result_url);
      } else {
        throw new Error('No video URL received from D-ID');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate avatar video';
      setError(errorMessage);
      onError?.(errorMessage);
      console.error('Avatar generation error:', err);
    } finally {
      setIsGenerating(false);
      setCurrentTalkId(null);
    }
  };

  const handleRetry = () => {
    setError(null);
    setVideoUrl(null);
    if (currentContent) {
      generateVideo(currentContent);
    }
  };

  const handleVideoEnded = () => {
    // Auto-pause when video ends
    onPlayPause();
  };

  const isCreditsError = error?.includes('Insufficient D-ID credits') || error?.includes('402');
  const isApiKeyError = error?.includes('Invalid D-ID API key') || error?.includes('401');
  const isNetworkError = error?.includes('Network error') || error?.includes('Failed to fetch');

  return (
    <div className="flex flex-col items-center space-y-8">
      {/* Avatar Video Container */}
      <div className="relative">
        <div className="w-64 h-64 rounded-full overflow-hidden bg-gradient-to-b from-blue-400 to-blue-600 flex items-center justify-center shadow-2xl border-4 border-white">
          <AnimatePresence mode="wait">
            {isGenerating ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex flex-col items-center space-y-4 text-white"
              >
                <Loader2 className="w-12 h-12 animate-spin" />
                <div className="text-center">
                  <p className="text-sm font-medium">Generating Avatar</p>
                  <p className="text-xs opacity-75">This may take 30-60 seconds</p>
                </div>
              </motion.div>
            ) : error ? (
              <motion.div
                key="error"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex flex-col items-center space-y-4 text-white p-4 text-center"
              >
                <AlertCircle className="w-12 h-12 text-red-300" />
                <div>
                  <p className="text-sm font-medium mb-2">Generation Failed</p>
                  <p className="text-xs opacity-75 mb-3 max-w-48">{error}</p>
                  
                  {/* Action buttons based on error type */}
                  <div className="flex flex-col space-y-2">
                    {!isCreditsError && !isApiKeyError && (
                      <button
                        onClick={handleRetry}
                        className="px-3 py-1 bg-white bg-opacity-20 rounded-lg text-xs hover:bg-opacity-30 transition-colors flex items-center justify-center space-x-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        <span>Retry</span>
                      </button>
                    )}
                    
                    {isCreditsError && (
                      <a
                        href="https://studio.d-id.com/account-settings"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 bg-yellow-500 bg-opacity-80 rounded-lg text-xs hover:bg-opacity-100 transition-colors flex items-center justify-center space-x-1 text-yellow-900"
                      >
                        <ExternalLink className="w-3 h-3" />
                        <span>Add Credits</span>
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ) : videoUrl ? (
              <motion.video
                key="video"
                ref={videoRef}
                src={videoUrl}
                className="w-full h-full object-cover"
                onEnded={handleVideoEnded}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                playsInline
                muted={!audioEnabled}
              />
            ) : (
              <motion.div
                key="placeholder"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex flex-col items-center text-white"
              >
                {/* Static Avatar Face */}
                <div className="relative">
                  {/* Eyes */}
                  <div className="flex space-x-8 mb-4">
                    <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-blue-900 rounded-full"></div>
                    </div>
                    <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-blue-900 rounded-full"></div>
                    </div>
                  </div>
                  
                  {/* Mouth */}
                  <div className="w-8 h-3 bg-white rounded-full mx-auto" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Status Indicator */}
        {isPlaying && videoUrl && (
          <motion.div
            className="absolute -bottom-2 left-1/2 transform -translate-x-1/2"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
          >
            <div className="flex space-x-1">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-2 h-2 bg-green-400 rounded-full"
                  animate={{
                    y: [0, -8, 0]
                  }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    delay: i * 0.2
                  }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Content Display */}
      <div className="max-w-2xl text-center">
        <div className="bg-gray-800 rounded-xl p-6 mb-6">
          <p className="text-lg text-gray-300 leading-relaxed">
            {isPlaying ? currentContent : "Press play to start your AI-powered training session"}
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={onPlayPause}
            disabled={isGenerating}
            className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-medium shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
              isPlaying 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isGenerating ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6 ml-1" />
            )}
          </button>

          <button
            onClick={() => setAudioEnabled(!audioEnabled)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              audioEnabled 
                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                : 'bg-gray-600 hover:bg-gray-700 text-gray-300'
            }`}
          >
            {audioEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>

          {videoUrl && (
            <button
              onClick={handleRetry}
              className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-600 hover:bg-gray-700 text-white transition-colors"
              title="Regenerate video"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* API Status */}
      <div className="text-center">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          🤖 AI Avatar {import.meta.env.VITE_DID_API_KEY ? 'Ready' : 'Demo Mode'}
        </span>
      </div>

      {/* Error-specific help messages */}
      {error && (
        <div className="max-w-md text-center">
          {isCreditsError && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 mb-2">
                <strong>Credits Required:</strong> Your D-ID account needs credits to generate avatars.
              </p>
              <a
                href="https://studio.d-id.com/account-settings"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-1 text-xs text-yellow-700 hover:text-yellow-900 underline"
              >
                <span>Add credits to your D-ID account</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
          
          {isApiKeyError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 mb-2">
                <strong>API Key Issue:</strong> Please check your D-ID API key configuration.
              </p>
              <p className="text-xs text-red-700">
                Verify your <code>VITE_DID_API_KEY</code> in the .env file is correct.
              </p>
            </div>
          )}
          
          {isNetworkError && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-800 mb-2">
                <strong>Connection Issue:</strong> Unable to reach D-ID servers.
              </p>
              <p className="text-xs text-orange-700">
                Please check your internet connection and try again.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Setup Instructions */}
      {!import.meta.env.VITE_DID_API_KEY && (
        <div className="max-w-md text-center bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800 mb-2">
            <strong>Setup Required:</strong> Add your D-ID API key to enable AI avatar generation.
          </p>
          <p className="text-xs text-yellow-700 mb-2">
            Create a <code>.env</code> file with: <code>VITE_DID_API_KEY=your_api_key</code>
          </p>
          <a
            href="https://studio.d-id.com/account-settings"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-1 text-xs text-yellow-700 hover:text-yellow-900 underline"
          >
            <span>Get your API key from D-ID Studio</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </div>
  );
}